import { Link } from "react-router-dom";
import { ArrowLeft, Info } from "lucide-react";
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  LearnerFormEditor,
  PageHeader,
  PageHeaderDescription,
  PageHeaderHeading,
  PageHeaderTitle,
  PageHeaderTop,
  toast,
} from "@eduviaos/ui-kit";
import { ApiError, type LearnerFormUpdateRequest } from "@eduviaos/shared";
import { useAuth } from "@/auth/AuthProvider";
import { useLearnerForm, useUpdateLearnerForm } from "@/hooks/useLearnerForm";
import { useTerms } from "@/hooks/useTerms";

/** Class-and-grade fields that a centre without classes never sees. */
const NO_GRADE_KEYS = ["grade", "section", "stage"] as const;

/**
 * Phase SC-22 — the institution's learner record form.
 *
 * Read-only unless the platform has handed editing to the institution
 * (`editable_by_tenant`) and the viewer is an admin or owner; the
 * backend enforces the same rule on `PUT /me/learner-form`.
 */
export function LearnerFormSettingsPage() {
  const { user } = useAuth();
  const { terms, profile } = useTerms();
  const formQuery = useLearnerForm();
  const save = useUpdateLearnerForm();

  const personOne = terms.learner.one;
  const title = `${personOne} form`;
  const isAdmin = user?.role === "admin" || user?.role === "owner";
  const form = formQuery.data;
  const canEdit = Boolean(form?.editable_by_tenant) && isAdmin;
  const omitKeys = profile.has.grades ? [] : NO_GRADE_KEYS;

  async function handleSave(body: LearnerFormUpdateRequest) {
    try {
      await save.mutateAsync(body);
      toast.success(`${title} saved.`);
    } catch (err) {
      const message =
        err instanceof ApiError && err.status === 403
          ? `Editing the ${title.toLowerCase()} was turned off. Refresh to see the latest version.`
          : err instanceof ApiError && err.status === 422
            ? "Some fields need another look. Check the names and choices, then save again."
            : `Couldn't save the ${title.toLowerCase()}. Try again in a moment.`;
      toast.error(message);
    }
  }

  return (
    <div className="mx-auto max-w-3xl space-y-6 p-4 pb-12 lg:p-8">
      <PageHeader>
        <PageHeaderTop>
          <Button variant="ghost" size="sm" asChild>
            <Link to="/settings">
              <ArrowLeft className="size-4" aria-hidden="true" />
              Settings
            </Link>
          </Button>
        </PageHeaderTop>
        <PageHeaderHeading>
          <PageHeaderTitle>{title}</PageHeaderTitle>
          <PageHeaderDescription>
            The details your team fills in when adding or editing a {personOne.toLowerCase()}, in
            the order they appear.
          </PageHeaderDescription>
        </PageHeaderHeading>
      </PageHeader>

      {formQuery.isLoading ? (
        <Card aria-busy="true">
          <CardContent className="grid gap-2 py-6">
            {Array.from({ length: 6 }, (_, i) => (
              <div
                key={i}
                className="bg-muted h-10 animate-pulse rounded-md motion-reduce:animate-none"
              />
            ))}
          </CardContent>
        </Card>
      ) : formQuery.isError || !form ? (
        <Card>
          <CardContent className="flex flex-wrap items-center justify-between gap-3 py-6 text-sm">
            <p className="text-destructive">Couldn&apos;t load the {title.toLowerCase()}.</p>
            <Button variant="outline" size="sm" onClick={() => void formQuery.refetch()}>
              Try again
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Fields</CardTitle>
            <CardDescription>
              {canEdit
                ? "Rename, reorder, hide or require fields, and add extra ones you need."
                : "Fields marked Required must be filled in before a record can be saved."}
            </CardDescription>
          </CardHeader>
          <CardContent className="grid gap-4">
            {!canEdit ? (
              <div
                role="note"
                className="bg-muted/40 text-muted-foreground flex items-start gap-2 rounded-lg border px-3 py-2.5 text-sm"
              >
                <Info className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
                <p>
                  {form.editable_by_tenant
                    ? `Only an admin can change the ${title.toLowerCase()}.`
                    : `This form is set up for you by EduViaOS. To add or change a field, message support and we'll update it.`}
                </p>
              </div>
            ) : null}
            <LearnerFormEditor
              form={form}
              readOnly={!canEdit}
              saving={save.isPending}
              onSave={(body) => void handleSave(body)}
              personNoun={personOne.toLowerCase()}
              omitKeys={omitKeys}
            />
          </CardContent>
        </Card>
      )}
    </div>
  );
}
